<template>
  <div class="official-dash-root">
    <MainNavbar />
    <ToastContainer :toasts="toasts" @remove="removeToast" />

    <div class="official-dash-page">
      <router-link :to="{ name: 'MatchesHub' }" class="official-dash-back">
        <i class="fa-solid fa-arrow-left" aria-hidden="true" />
        Back to Match Hub
      </router-link>

      <header class="official-dash-header">
        <div class="official-dash-header__main">
          <p class="official-dash-kicker">
            <i class="fa-solid fa-futbol" aria-hidden="true" />
            IpromiseAfrica Match Hub · Players
          </p>
          <h1 class="official-dash-title">Player dashboard</h1>
          <p class="official-dash-subtitle">
            View upcoming fixtures for your teams, review match tactics, and manage team invitations.
          </p>
        </div>
        <div v-if="hasLoadedOnce" class="player-dash-header-actions">
          <div class="official-dash-profile-chip" aria-label="Your player profile">
            <span class="official-dash-profile-chip__role">{{ playerInfo.name || "Player" }}</span>
            <span v-if="profileMeta" class="official-dash-profile-chip__meta">{{ profileMeta }}</span>
          </div>
          <button
            type="button"
            class="official-dash-btn official-dash-btn--ghost official-dash-btn--sm"
            :disabled="loading"
            @click="loadDashboardData({ silentToast: true })"
          >
            <i class="fa-solid fa-sync-alt" :class="{ 'fa-spin': loading }" aria-hidden="true" />
            Refresh
          </button>
        </div>
      </header>

      <div v-if="loading && !hasLoadedOnce" class="official-dash-loading" aria-live="polite">
        <i class="fa-solid fa-spinner fa-spin" aria-hidden="true" />
        Loading your dashboard…
      </div>

      <template v-else-if="hasLoadedOnce">
        <section class="official-dash-stats" aria-label="Overview statistics">
          <article v-for="item in statCards" :key="item.id" class="official-dash-stat">
            <span
              class="official-dash-stat__icon"
              :class="`official-dash-stat__icon--${item.tone}`"
              aria-hidden="true"
            >
              <i :class="item.icon" />
            </span>
            <span class="official-dash-stat__value">{{ item.value }}</span>
            <span class="official-dash-stat__label">{{ item.label }}</span>
          </article>
        </section>

        <section
          v-if="pendingInvites.length > 0 || pendingMemberships.length > 0"
          class="official-dash-panel player-pending-panel"
          aria-labelledby="player-pending-heading"
        >
          <div class="official-dash-panel__head">
            <div class="official-dash-panel__title-wrap">
              <h2 id="player-pending-heading" class="official-dash-panel__title">
                <i class="fa-solid fa-envelope-open-text" aria-hidden="true" />
                Team invitations & pending membership
              </h2>
              <p class="official-dash-panel__desc">
                You can only see match and tactics data after you are an
                <strong>active</strong> member of a team. Accept an invite below, or wait until
                your membership is activated.
              </p>
            </div>
          </div>

          <div class="official-dash-list player-pending-list" role="list">
            <article
              v-for="inv in pendingInvites"
              :key="'invite-' + inv.invite_id"
              class="official-dash-card player-pending-card"
              role="listitem"
            >
              <div class="player-pending-card__body">
                <span class="player-pending-card__icon" aria-hidden="true">
                  <i class="fa-solid fa-inbox" />
                </span>
                <div>
                  <h3 class="player-pending-card__title">Invitation pending</h3>
                  <p class="player-pending-card__text">
                    <strong>{{ inv.team_name || "A team" }}</strong>
                    <span v-if="inv.sender_name"> — from {{ inv.sender_name }}</span>
                  </p>
                  <p v-if="inv.expires_at" class="player-pending-card__meta">
                    <i class="fa-solid fa-clock" aria-hidden="true" />
                    Expires {{ formatDate(inv.expires_at) }}
                  </p>
                </div>
              </div>
              <div class="official-dash-card__actions">
                <button
                  type="button"
                  class="official-dash-btn official-dash-btn--primary official-dash-btn--sm"
                  :disabled="processingInviteId != null"
                  @click="acceptTeamInvite(inv.invite_id)"
                >
                  <i
                    v-if="processingInviteId === inv.invite_id"
                    class="fa-solid fa-spinner fa-spin"
                    aria-hidden="true"
                  />
                  <i v-else class="fa-solid fa-check" aria-hidden="true" />
                  Accept
                </button>
                <button
                  type="button"
                  class="official-dash-btn official-dash-btn--ghost official-dash-btn--sm"
                  :disabled="processingInviteId != null"
                  @click="declineTeamInvite(inv.invite_id)"
                >
                  <i
                    v-if="processingInviteId === -inv.invite_id"
                    class="fa-solid fa-spinner fa-spin"
                    aria-hidden="true"
                  />
                  <i v-else class="fa-solid fa-times" aria-hidden="true" />
                  Decline
                </button>
              </div>
            </article>

            <article
              v-for="m in pendingMemberships"
              :key="'mem-' + m.team_id + '-' + (m.member_status || '')"
              class="official-dash-card player-pending-card player-pending-card--muted"
              role="listitem"
            >
              <div class="player-pending-card__body">
                <span class="player-pending-card__icon player-pending-card__icon--muted" aria-hidden="true">
                  <i class="fa-solid fa-hourglass-half" />
                </span>
                <div>
                  <h3 class="player-pending-card__title">Membership not active</h3>
                  <p class="player-pending-card__text">
                    Your place on <strong>{{ m.name }}</strong> is still
                    <span class="player-status-pill">{{ m.member_status || "pending" }}</span>.
                    You cannot access this team's matches or tactics until your status is
                    <strong>active</strong>.
                  </p>
                </div>
              </div>
            </article>
          </div>
        </section>

        <div class="official-dash-layout">
          <div class="official-dash-layout__primary">
            <section class="official-dash-panel" aria-labelledby="player-matches-heading">
              <div class="official-dash-panel__head">
                <div class="official-dash-panel__title-wrap">
                  <h2 id="player-matches-heading" class="official-dash-panel__title">
                    <i class="fa-solid fa-calendar" aria-hidden="true" />
                    Upcoming matches
                  </h2>
                  <p class="official-dash-panel__desc">
                    Fixtures for teams where you are an active member.
                  </p>
                </div>
                <span class="official-dash-badge official-dash-badge--soon">
                  {{ upcomingMatches.length }}
                </span>
              </div>

              <div class="official-dash-list" role="list">
                <p v-if="!upcomingMatches.length" class="official-dash-empty" role="status">
                  <template v-if="pendingInvites.length > 0 || pendingMemberships.length > 0">
                    No upcoming matches yet. Accept a team invitation to become an active member and
                    see matches and tactics here.
                  </template>
                  <template v-else>
                    You don't have any upcoming matches scheduled.
                  </template>
                </p>

                <article
                  v-for="match in upcomingMatches"
                  :key="match.match_id"
                  class="official-dash-card player-match-card"
                  role="listitem"
                >
                  <div class="official-dash-card__top">
                    <span class="official-dash-card__sport">Match #{{ match.match_id }}</span>
                    <span
                      class="official-dash-badge"
                      :class="matchStatusBadgeClass(match.status)"
                    >
                      {{ formatMatchStatus(match.status) }}
                    </span>
                  </div>
                  <p class="official-dash-card__teams">
                    {{ match.team_name }}
                    <span class="official-dash-card__vs">vs</span>
                    {{ match.opponent_name }}
                  </p>
                  <p class="player-match-side">
                    {{ match.is_home ? "Home" : "Away" }} fixture
                  </p>
                  <p class="official-dash-card__meta">
                    <span>
                      <i class="fa-solid fa-clock" aria-hidden="true" />
                      {{ formatDate(match.match_date) }} · {{ formatTime(match.match_date) }}
                    </span>
                    <span>
                      <i class="fa-solid fa-hourglass-half" aria-hidden="true" />
                      {{ formatDuration(match.duration) }}
                    </span>
                    <span
                      v-if="match.venue"
                      class="official-dash-card__venue"
                    >
                      <i class="fa-solid fa-map-marker-alt" aria-hidden="true" />
                      <span class="official-dash-venue-info">
                        <span class="official-dash-venue-text">
                          {{ match.venue.name }}{{ match.venue.city ? ", " + match.venue.city : "" }}
                        </span>
                        <a
                          v-if="match.venue.notes"
                          :href="match.venue.notes"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="official-dash-venue-link"
                          @click.stop
                        >
                          <i class="fa-solid fa-map-location-dot" aria-hidden="true" />
                          View pinned location
                        </a>
                      </span>
                    </span>
                  </p>
                  <div class="official-dash-card__actions">
                    <button
                      type="button"
                      class="official-dash-btn official-dash-btn--ghost official-dash-btn--sm"
                      @click.stop="viewMatchDetails(match)"
                    >
                      <i class="fa-solid fa-eye" aria-hidden="true" />
                      Details
                    </button>
                    <button
                      v-if="canViewMatchTactics(match)"
                      type="button"
                      class="official-dash-btn official-dash-btn--primary official-dash-btn--sm"
                      :disabled="loadingTactics[match.match_id]"
                      @click.stop="toggleTacticsView(match.match_id)"
                    >
                      <i class="fa-solid fa-chess-board" aria-hidden="true" />
                      <span v-if="loadingTactics[match.match_id]">Loading…</span>
                      <span v-else>{{ expandedTactics[match.match_id] ? "Hide" : "View" }} tactics</span>
                    </button>
                  </div>

                  <div v-if="expandedTactics[match.match_id]" class="player-tactics-view">
                    <div
                      v-if="matchTactics[match.match_id] && matchTactics[match.match_id].player_positions"
                      class="player-tactics-content"
                    >
                      <div class="player-tactics-header">
                        <span class="player-tactics-label">Formation:</span>
                        <span class="player-tactics-formation">
                          {{ matchTactics[match.match_id].formation_name || "N/A" }}
                        </span>
                        <span
                          v-if="matchTactics[match.match_id].formation_category"
                          class="player-tactics-category"
                        >
                          ({{ capitalizeText(matchTactics[match.match_id].formation_category) }})
                        </span>
                      </div>
                      <div class="pitch-container-view">
                        <div class="pitch-wrapper-view">
                          <div class="soccer-pitch-view">
                            <img
                              src="@/assets/images/football_pitch/footballpitch.png"
                              alt="Football pitch"
                              class="pitch-image-view"
                              loading="lazy"
                              decoding="async"
                            />
                            <div
                              v-for="(position, index) in matchTactics[match.match_id].player_positions"
                              :key="index"
                              class="player-position-view"
                              :class="{ 'goalkeeper-position': position.role === 'goalkeeper' }"
                              :style="getTacticsPositionStyle(position)"
                            >
                              <div v-if="position.player_id" class="player-marker-view" :class="position.role">
                                <div class="player-number-view">{{ position.player_number || "?" }}</div>
                                <div class="player-name-tag-view">{{ position.player_name || "Player" }}</div>
                              </div>
                              <div v-else class="empty-position-view">
                                <i class="fa-solid fa-plus" aria-hidden="true" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else-if="!loadingTactics[match.match_id]" class="player-tactics-empty">
                      <i class="fa-solid fa-info-circle" aria-hidden="true" />
                      <p>No tactics have been set for this match yet.</p>
                    </div>
                    <div v-else class="player-tactics-loading">
                      <i class="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                      <p>Loading tactics…</p>
                    </div>
                  </div>
                </article>
              </div>
            </section>
          </div>

          <aside class="official-dash-layout__aside">
            <section class="official-dash-panel" aria-labelledby="player-teams-heading">
              <div class="official-dash-panel__head">
                <div class="official-dash-panel__title-wrap">
                  <h2 id="player-teams-heading" class="official-dash-panel__title">
                    <i class="fa-solid fa-users" aria-hidden="true" />
                    My teams
                  </h2>
                  <p class="official-dash-panel__desc">Teams where you are an active member.</p>
                </div>
              </div>

              <div class="official-dash-list" role="list">
                <p v-if="!teams.length" class="official-dash-empty" role="status">
                  <template v-if="pendingInvites.length > 0 || pendingMemberships.length > 0">
                    No active teams yet. Accept a team invitation above (or wait until your
                    membership is active) to see your teams here.
                  </template>
                  <template v-else>
                    You're not a member of any teams yet.
                  </template>
                </p>

                <article
                  v-for="team in teams"
                  :key="team.team_id"
                  class="official-dash-card player-team-card"
                  role="listitem"
                >
                  <div class="player-team-card__head">
                    <div class="player-team-card__logo">
                      <img
                        v-if="team.logo_url"
                        :src="team.logo_url"
                        :alt="team.name"
                        loading="lazy"
                        decoding="async"
                        @error="handleImageError"
                      />
                      <i v-else class="fa-solid fa-users" aria-hidden="true" />
                    </div>
                    <div class="player-team-card__info">
                      <h3 class="player-team-card__name">{{ team.name }}</h3>
                      <p class="player-team-card__meta">
                        <span v-if="team.position">
                          <i class="fa-solid fa-user-tag" aria-hidden="true" />
                          {{ team.position }}
                        </span>
                        <span :class="{ 'player-team-card__captain': team.is_captain }">
                          <i v-if="team.is_captain" class="fa-solid fa-star" aria-hidden="true" />
                          {{ capitalizeText(team.member_role) }}
                        </span>
                      </p>
                      <p class="player-team-card__location">
                        <i class="fa-solid fa-map-marker-alt" aria-hidden="true" />
                        {{ team.county }}{{ team.constituency ? ", " + team.constituency : "" }}
                      </p>
                    </div>
                  </div>
                  <div class="official-dash-card__actions">
                    <button
                      type="button"
                      class="official-dash-btn official-dash-btn--primary official-dash-btn--sm"
                      @click="viewTeamDashboard(team.team_id)"
                    >
                      View team
                      <i class="fa-solid fa-arrow-right" aria-hidden="true" />
                    </button>
                  </div>
                </article>
              </div>
            </section>
          </aside>
        </div>
      </template>
    </div>

    <ThirdpartyFooter />
  </div>
</template>

<script>
import MainNavbar from "@/components/main/headernfooter/header.vue";
import ThirdpartyFooter from "@/routes/thirdparty/Footer.vue";
import globalLoadingMixin from "@/mixins/globalLoadingMixin";
import ToastContainer from "@/components/ui/ToastContainer.vue";
import toastMixin from "@/mixins/toastMixin";
import { buildApiUrl } from "@/config/api";

export default {
  name: "PlayerDashboard",
  mixins: [toastMixin, globalLoadingMixin],
  components: {
    MainNavbar,
    ThirdpartyFooter,
    ToastContainer,
  },
  data() {
    return {
      loading: false,
      hasLoadedOnce: false,
      loadingMessage: "Loading player dashboard...",
      playerInfo: {
        customer_number: null,
        name: '',
        email: '',
        phone: ''
      },
      teams: [],
      pendingInvites: [],
      pendingMemberships: [],
      processingInviteId: null,
      upcomingMatches: [],
      expandedTactics: {},
      matchTactics: {},
      loadingTactics: {}
    };
  },
  computed: {
    statCards() {
      const pendingCount = this.pendingInvites.length + this.pendingMemberships.length;
      return [
        {
          id: "teams",
          icon: "fa-solid fa-users",
          tone: "officiated",
          value: this.teams.length,
          label: "Active teams",
        },
        {
          id: "upcoming",
          icon: "fa-solid fa-calendar-days",
          tone: "upcoming",
          value: this.upcomingMatches.length,
          label: "Upcoming matches",
        },
        {
          id: "pending",
          icon: "fa-solid fa-envelope-open-text",
          tone: "rating",
          value: pendingCount,
          label: "Pending invitations",
        },
      ];
    },
    profileMeta() {
      const parts = [this.playerInfo.email, this.playerInfo.phone].filter(Boolean);
      return parts.join(" · ");
    },
  },
  async mounted() {
    await this.loadDashboardData();
  },
  methods: {
    getAuthToken() {
      return (
        localStorage.getItem('access_token') ||
        localStorage.getItem('token') ||
        sessionStorage.getItem('access_token') ||
        sessionStorage.getItem('token')
      );
    },
    
    async loadDashboardData(options = {}) {
      const { silentToast = false } = options;
      this.loading = true;
      this.loadingMessage = 'Loading player dashboard...';
      
      try {
        const token = this.getAuthToken();
        if (!token) {
          this.showToast(
            'error',
            'Authentication Required',
            'Please log in to view your player dashboard.'
          );
          setTimeout(() => {
            this.$router.push('/ipromiseafrica/sign-in');
          }, 2000);
          return;
        }
        
        const response = await fetch(buildApiUrl('/api/players/dashboard'), {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });
        
        if (!response.ok) {
          if (response.status === 401) {
            this.showToast(
              'error',
              'Session Expired',
              'Please log in again to continue.'
            );
            setTimeout(() => {
              this.$router.push('/ipromiseafrica/sign-in');
            }, 2000);
            return;
          }
          
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `Failed to load dashboard: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Set player info
        this.playerInfo = {
          customer_number: data.player?.customer_number || null,
          name: data.player?.name || 'Player',
          email: data.player?.email || '',
          phone: data.player?.phone || ''
        };
        
        // Set teams (active members only — from API)
        this.teams = data.teams || [];
        this.pendingInvites = data.pending_invites || [];
        this.pendingMemberships = data.pending_memberships || [];

        // Set upcoming matches (active members only — from API)
        this.upcomingMatches = data.upcoming_matches || [];
        
        // Initialize tactics data structures
        this.upcomingMatches.forEach(match => {
          this.expandedTactics[match.match_id] = false;
          this.matchTactics[match.match_id] = null;
          this.loadingTactics[match.match_id] = false;
        });
        
        if (!silentToast) {
          const welcome =
            this.teams.length === 0 &&
            (this.pendingInvites.length > 0 || this.pendingMemberships.length > 0)
              ? 'You have pending invitations or membership — accept an invite to access team matches.'
              : `Welcome back, ${this.playerInfo.name}!`;
          this.showToast('success', 'Dashboard Loaded', welcome);
        }
        
      } catch (error) {
        console.error('Error loading player dashboard:', error);
        this.showToast(
          'error',
          'Failed to Load Dashboard',
          error.message || 'Please try again later.'
        );
      } finally {
        this.loading = false;
        this.hasLoadedOnce = true;
      }
    },
    
    matchStatusBadgeClass(status) {
      const key = (status || "").toLowerCase();
      const map = {
        pending: "player-match-badge--pending",
        confirmed: "player-match-badge--confirmed",
        scheduled: "player-match-badge--scheduled",
        past: "player-match-badge--past",
        completed: "player-match-badge--past",
        cancelled: "player-match-badge--cancelled",
      };
      return map[key] || "official-dash-badge--soon";
    },

    viewMatchDetails(match) {
      if (!match?.match_id || match.team_id == null) return;
      this.$router.push({
        name: 'MatchDetails',
        params: { match_id: String(match.match_id) },
        query: { team_id: String(match.team_id) },
      }).catch(() => {});
    },

    canViewMatchTactics(match) {
      const s = (match?.status || '').toLowerCase();
      return ['confirmed', 'scheduled'].includes(s);
    },
    
    viewTeamDashboard(teamId) {
      this.$router.push(`/ipromiseafrica/matchup/football/dashboard?team_id=${teamId}`);
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    
    formatTime(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    formatDuration(minutes) {
      if (!minutes) return 'N/A';
      const mins = parseInt(minutes);
      return `${mins} minute${mins !== 1 ? 's' : ''}`;
    },
    
    formatMatchStatus(status) {
      if (!status) return 'Unknown';
      const statusMap = {
        'pending': 'Pending',
        'confirmed': 'Confirmed',
        'scheduled': 'Scheduled',
        'past': 'Past',
        'completed': 'Completed',
        'cancelled': 'Cancelled'
      };
      return statusMap[status.toLowerCase()] || this.capitalizeText(status);
    },
    
    capitalizeText(text) {
      if (!text) return '';
      return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    },
    
    handleImageError(event) {
      event.target.style.display = 'none';
      const icon = document.createElement('i');
      icon.className = 'fa-solid fa-users';
      event.target.parentElement.appendChild(icon);
    },
    

    async acceptTeamInvite(inviteId) {
      if (this.processingInviteId != null || !inviteId) return;
      const token = this.getAuthToken();
      if (!token) {
        this.showToast('error', 'Sign in required', 'Please log in again.');
        return;
      }
      this.processingInviteId = inviteId;
      try {
        const response = await fetch(
          buildApiUrl(`/api/teams/invites/${inviteId}/accept`),
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        const errBody = await response.json().catch(() => ({}));
        if (!response.ok) {
          const detail = errBody.detail;
          const msg =
            typeof detail === 'string'
              ? detail
              : `Could not accept invite (${response.status})`;
          throw new Error(msg);
        }
        this.showToast('success', 'Joined team', errBody.message || 'You are now an active member.');
        await this.loadDashboardData({ silentToast: true });
      } catch (e) {
        console.error(e);
        this.showToast('error', 'Invite', e.message || 'Failed to accept invite.');
      } finally {
        this.processingInviteId = null;
      }
    },

    async declineTeamInvite(inviteId) {
      if (this.processingInviteId != null || !inviteId) return;
      const token = this.getAuthToken();
      if (!token) {
        this.showToast('error', 'Sign in required', 'Please log in again.');
        return;
      }
      this.processingInviteId = -inviteId;
      try {
        const response = await fetch(
          buildApiUrl(`/api/teams/invites/${inviteId}/reject`),
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        const errBody = await response.json().catch(() => ({}));
        if (!response.ok) {
          const detail = errBody.detail;
          const msg =
            typeof detail === 'string'
              ? detail
              : `Could not decline invite (${response.status})`;
          throw new Error(msg);
        }
        this.showToast('success', 'Invite declined', errBody.message || 'Invitation declined.');
        await this.loadDashboardData({ silentToast: true });
      } catch (e) {
        console.error(e);
        this.showToast('error', 'Invite', e.message || 'Failed to decline invite.');
      } finally {
        this.processingInviteId = null;
      }
    },

    async toggleTacticsView(matchId) {
      // Toggle expanded state
      this.expandedTactics[matchId] = !this.expandedTactics[matchId];
      
      // If expanding and tactics not loaded, fetch them
      if (this.expandedTactics[matchId] && !this.matchTactics[matchId]) {
        await this.loadMatchTactics(matchId);
      }
    },
    
    async loadMatchTactics(matchId) {
      const match = this.upcomingMatches.find(m => m.match_id === matchId);
      if (!match || !match.team_id) return;
      
      this.loadingTactics[matchId] = true;
      
      try {
        const token = this.getAuthToken();
        if (!token) {
          throw new Error('Authentication required');
        }
        
        const response = await fetch(
          buildApiUrl(`/api/tactics/matches/${matchId}/teams/${match.team_id}`),
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            }
          }
        );
        
        if (!response.ok) {
          if (response.status === 404) {
            // No tactics set yet
            this.matchTactics[matchId] = null;
            return;
          }
          if (response.status === 403) {
            this.showToast(
              'error',
              'Tactics unavailable',
              'You must be an active team member to view tactics.'
            );
            this.expandedTactics[matchId] = false;
            return;
          }
          throw new Error(`Failed to load tactics: ${response.status}`);
        }
        
        const data = await response.json();
        const tactics = data.tactics;
        
        if (tactics && tactics.player_positions) {
          // Map player positions with player info
          // For now, we'll display player_id as the number since we don't have direct access to player names
          // The player_id is the team_member_number from the database
          const tacticsWithPlayerInfo = {
            formation_name: tactics.formation_name,
            formation_category: tactics.formation_category,
            player_positions: tactics.player_positions.map((pos) => {
              if (pos.player_id) {
                // Use last 2 digits of player_id as a simple number display
                // In production, you'd want to fetch actual player names
                const displayNumber = pos.player_id % 100;
                return {
                  ...pos,
                  player_id: pos.player_id,
                  player_number: displayNumber,
                  player_name: `#${displayNumber}`
                };
              }
              return pos;
            })
          };
          
          this.matchTactics[matchId] = tacticsWithPlayerInfo;
        } else {
          this.matchTactics[matchId] = null;
        }
        
      } catch (error) {
        console.error('Error loading match tactics:', error);
        this.showToast(
          'error',
          'Failed to Load Tactics',
          error.message || 'Could not load tactics for this match.'
        );
        this.matchTactics[matchId] = null;
      } finally {
        this.loadingTactics[matchId] = false;
      }
    },
    
    getTacticsPositionStyle(position) {
      return {
        left: `${position.position_x}%`,
        top: `${position.position_y}%`,
        transform: 'translate(-50%, -50%)'
      };
    }
  }
};
</script>

<style scoped src="@/routes/match_officials/officialDashboard.css"></style>
<style scoped>
.player-dash-header-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 12px;
}

.player-pending-panel {
  margin-bottom: clamp(20px, 4vw, 28px);
}

.player-pending-list {
  max-height: none;
}

.player-pending-card {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.player-pending-card__body {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  flex: 1;
  min-width: 200px;
}

.player-pending-card__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: color-mix(in srgb, #2563eb 14%, var(--background-color3));
  color: #2563eb;
  flex-shrink: 0;
}

.player-pending-card__icon--muted {
  background: var(--background-color3);
  color: var(--text-color2);
}

.player-pending-card__title {
  margin: 0 0 6px;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-color1);
}

.player-pending-card__text,
.player-pending-card__meta {
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.45;
  color: var(--text-color2);
}

.player-pending-card__meta {
  margin-top: 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.player-status-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  background: color-mix(in srgb, #f59e0b 16%, var(--background-color3));
  color: #d97706;
  text-transform: lowercase;
  font-size: 0.9em;
  font-weight: 600;
}

.player-match-side {
  margin: -4px 0 0;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--border-color-hover);
}

.player-match-badge--pending {
  background: color-mix(in srgb, #f59e0b 16%, var(--background-color3));
  color: #d97706;
}

.player-match-badge--confirmed {
  background: color-mix(in srgb, #43a047 16%, var(--background-color3));
  color: #43a047;
}

.player-match-badge--scheduled {
  background: color-mix(in srgb, #2563eb 14%, var(--background-color3));
  color: #2563eb;
}

.player-match-badge--past {
  background: var(--background-color3);
  color: var(--text-color2);
}

.player-match-badge--cancelled {
  background: color-mix(in srgb, #e53935 14%, var(--background-color3));
  color: #e53935;
}

.player-tactics-view {
  margin-top: 4px;
  padding: clamp(14px, 3vw, 18px);
  background: var(--background-color1);
  border: 1px solid var(--border-color-hover1);
  border-radius: 12px;
}

.player-tactics-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--border-color-hover1);
}

.player-tactics-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-color2);
}

.player-tactics-formation {
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-color1);
}

.player-tactics-category {
  font-size: 0.85rem;
  color: var(--text-color2);
}

.pitch-container-view {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  padding-bottom: 60%;
}

.pitch-wrapper-view {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.soccer-pitch-view {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.pitch-image-view {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  user-select: none;
  pointer-events: none;
}

.player-position-view {
  position: absolute;
  width: 50px;
  height: 50px;
  z-index: 10;
}

.player-marker-view {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 3px solid;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  position: relative;
}

.player-marker-view.forward {
  background: linear-gradient(135deg, #ff6b6b, #ee5a6f);
  border-color: #ff4757;
}

.player-marker-view.midfielder {
  background: linear-gradient(135deg, #4ecdc4, #44a08d);
  border-color: #00d2d3;
}

.player-marker-view.defender {
  background: linear-gradient(135deg, #95a5a6, #7f8c8d);
  border-color: #636e72;
}

.player-marker-view.goalkeeper {
  background: linear-gradient(135deg, #feca57, #ff9ff3);
  border-color: #ff6348;
  border-width: 4px;
  z-index: 15;
}

.player-number-view {
  font-weight: 700;
  font-size: 0.9rem;
  color: white;
  line-height: 1;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}

.player-name-tag-view {
  position: absolute;
  bottom: -20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.7rem;
  white-space: nowrap;
  font-weight: 600;
  pointer-events: none;
}

.empty-position-view {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 2px dashed var(--border-color-hover1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-color2);
  background: var(--background-color2);
}

.player-tactics-empty,
.player-tactics-loading {
  text-align: center;
  padding: 28px 16px;
  color: var(--text-color2);
}

.player-tactics-empty i,
.player-tactics-loading i {
  display: block;
  font-size: 1.5rem;
  margin-bottom: 10px;
  opacity: 0.7;
}

.player-tactics-loading i {
  color: var(--border-color-hover);
}

.player-tactics-empty p,
.player-tactics-loading p {
  margin: 0;
  font-size: 0.88rem;
}

.player-team-card__head {
  display: flex;
  gap: 14px;
  align-items: flex-start;
}

.player-team-card__logo {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: var(--background-color1);
  border: 1px solid var(--border-color-hover1);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  color: var(--border-color-hover);
}

.player-team-card__logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.player-team-card__name {
  margin: 0 0 6px;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-color1);
}

.player-team-card__meta,
.player-team-card__location {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  margin: 0;
  font-size: 0.8rem;
  color: var(--text-color2);
}

.player-team-card__meta span,
.player-team-card__location {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.player-team-card__captain {
  color: #e6a800;
  font-weight: 600;
}

@media (max-width: 768px) {
  .player-position-view {
    width: 40px;
    height: 40px;
  }

  .player-dash-header-actions {
    width: 100%;
  }
}
</style>
